import React, { useEffect, useState } from 'react'
import Modal, { ModalProps } from 'components/modal'
import api from 'helpers/api'
import { IContext, IProject, ITableViewColumn, ITableViewWithColumns } from 'types'
import Select from 'components/select'
import Button from 'components/button'
import { useApplicationStore } from 'hooks/application'
import useProject from 'hooks/useProject'
import { PROJECT } from 'app-constants'
import { ISpreadsheetData, SpreadsheetReducerActions } from 'components/spreadsheet/types'
import { capitaliseFirstLetter } from 'helpers/utils'
import { convertToApiColour, convertToApiFilter } from 'components/spreadsheet/helpers/functions'
import { SELECT_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { useNavigate, generatePath } from 'react-router-dom'
import { TABLE } from 'routes'

interface DuplicateProps extends ModalProps {
  // A hub is duplicated from its settings, in its own dialog (DuplicateHubDialog).
  type: 'table' | 'process' | 'view'
  publicId: string
  parentPublicId?: string
  spreadsheetData?: ISpreadsheetData
  setSpreadsheetData?: React.Dispatch<SpreadsheetReducerActions>
  isLockedTemplate?: boolean
  inProcess?: boolean
}

const Duplicate: React.FC<DuplicateProps> = ({
  id,
  open,
  setOpen,
  type,
  publicId,
  parentPublicId,
  spreadsheetData,
  setSpreadsheetData,
  isLockedTemplate,
  inProcess
}) => {
  const navigate = useNavigate()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project } = useProject()

  const [projects, setProjects] = useState<IProject[]>()
  const [targetProjectId, setTargetProjectId] = useState<string>()
  const [duplicateTables, setDuplicateTables] = useState<boolean>(id === 'use-template' ? true : false)
  const [duplicatePermissions, setDuplicatePermissions] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)

  const isDifferentHub = project.publicId !== targetProjectId
  const hideAdvancedOptions = isLockedTemplate && isDifferentHub

  useEffect(() => {
    if (project && project.publicId !== '') {
      setTargetProjectId(project.publicId)
    }
  }, [project?.publicId])

  useEffect(() => {
    api
      .getOwnerProjects()
      .then((response) => {
        if (response && response.data) {
          setProjects(
            response.data
              .filter((project: IProject) => !project.isDeleted)
              .sort((a: IProject, b: IProject) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
          )
        }
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
  }, [])

  const duplicate = () => {
    setSaving(true)
    if (targetProjectId) {
      if (type === 'table') {
        api
          .duplicateTable({
            publicId,
            targetProjectId,
            duplicatePermissions,
            duplicateTables
          })
          .then(() => {
            setSnackbarMessage({
              status: 'success',
              message: 'Duplication has started. You will receive an email once it has been complete.'
            })
            setSaving(false)
            setOpen(false)
          })
          .catch((error) => {
            setSaving(false)
            displayErrorMessage(error)
          })
      } else if (type === 'process') {
        api
          .duplicateProcess({
            publicId,
            targetProjectId,
            duplicatePermissions,
            duplicateTables,
            lockResource: false
          })
          .then(() => {
            setSnackbarMessage({
              status: 'success',
              message: 'Duplication has started. You will receive an email once it has been complete.'
            })
            setSaving(false)
            setOpen(false)
          })
          .catch((error) => {
            setSaving(false)
            displayErrorMessage(error)
          })
      } else if (type === 'view' && spreadsheetData && setSpreadsheetData) {
        if (parentPublicId === undefined || setSpreadsheetData === undefined) {
          setSnackbarMessage({ status: 'error' })
        }
        api
          .duplicateView(parentPublicId!, {
            publicId,
            duplicatePermissions
          })
          .then((response) => {
            const createdView = response.data as ITableViewWithColumns
            setSpreadsheetData!({ type: 'ADD_VIEW', view: createdView, inProcess: inProcess ? true : false })
            if (!inProcess) {
              navigate(
                generatePath(TABLE, {
                  projectId: project.publicId,
                  tableId: spreadsheetData.tableDetails.publicId,
                  viewId: response.data.publicId
                }),
                {
                  replace: true
                }
              )
            }

            const newTableView: Partial<ITableViewWithColumns> & IContext = {
              name: response.data.name,
              type: spreadsheetData.viewDetails.type,
              description: spreadsheetData.viewDetails.description,
              filterSettings: convertToApiFilter(spreadsheetData.userConfiguration.filterSettings),
              groupSettings: spreadsheetData.userConfiguration.groupSettings,
              sortSettings: spreadsheetData.userConfiguration.sortSettings,
              colourSettings: convertToApiColour(spreadsheetData.userConfiguration.colourSettings),
              chartSettings: spreadsheetData.userConfiguration.chartSettings,
              frozenIndex: spreadsheetData.viewDetails.frozenIndex,
              displayValidationErrorRows: spreadsheetData.viewDetails.displayValidationErrorRows,
              displayCommentRows: spreadsheetData.viewDetails.displayCommentRows,
              collapsedGroupView: spreadsheetData.viewDetails.collapsedGroupView,
              unpackMultiselectGroupView: spreadsheetData.viewDetails.unpackMultiselectGroupView,
              rowHeight: spreadsheetData.viewDetails.rowHeight,
              columns: spreadsheetData.viewDetails.columns
                .map((column) => {
                  const newColumn = JSON.parse(JSON.stringify(column))
                  if (newColumn['required'] === undefined) newColumn['required'] = false
                  if (SELECT_COLUMN_TYPES.includes(newColumn.kind) && newColumn.kindOptions?.tableOptions)
                    newColumn.kindOptions.tableOptions = {
                      ...newColumn.kindOptions.tableOptions,
                      cachedOptions: undefined
                    }

                  return newColumn
                })
                .filter(
                  (column: ITableViewColumn) =>
                    !spreadsheetData.userConfiguration.hiddenColumns.includes(column.publicId)
                ),
              context: { projectId: project.publicId }
            }

            return api.updateWholeTableView(createdView.publicId, newTableView)
          })
          .then(() => {
            setSnackbarMessage({
              status: 'success',
              message: 'The view has been successfully duplicated.'
            })
            setSaving(false)
            setOpen(false)
          })
          .catch((error) => {
            setSaving(false)
            displayErrorMessage(error)
          })
      }
    }
  }

  const typeName = type === 'table' ? 'table' : type === 'process' ? 'document' : type

  return (
    <Modal
      id={id}
      open={open}
      setOpen={setOpen}
      title={id === 'use-template' ? 'Use Template' : `Duplicate ${capitaliseFirstLetter(typeName)}`}
    >
      {(type === 'table' || type === 'process') && (
        <div>
          <div className="mb-[30px]">
            {id === 'use-template'
              ? `Please select the ${PROJECT} you want to use this template in. Only hubs in which you are
            'administrator' or 'owner' will be shown.`
              : `Please select the ${PROJECT} you want to duplicate this ${typeName} to. Only hubs in which you are
            'administrator' or 'owner' will be shown.`}
          </div>
          <Select
            options={
              projects
                ? projects.map((project) => {
                    return { value: project.publicId, label: project.name }
                  })
                : []
            }
            optionsSelected={targetProjectId ? [targetProjectId] : []}
            onOptionClick={(option: string) => {
              const validProjectIds = projects ? projects.map((p) => p.publicId) : []
              if (validProjectIds.includes(option)) {
                setTargetProjectId(option)
                setDuplicateTables(project.publicId !== option)
              }
            }}
            loading={projects === undefined}
          />
        </div>
      )}

      {(type !== 'process' || (type === 'process' && !hideAdvancedOptions)) && (
        <div>
          <div className="border-b-2 border-divider py-2.5 mb-4 mt-8 font-bold">Advanced Options</div>
          {type !== 'view' && !isDifferentHub && (
            <div className="flex items-center justify-center mt-5">
              <div>Do you want to duplicate all linked tables?</div>
              <div className="ml-auto w-[150px]">
                <Select
                  options={[
                    { value: 'Yes', label: 'Yes' },
                    { value: 'No', label: 'No' }
                  ]}
                  optionsSelected={[duplicateTables ? 'Yes' : 'No']}
                  onOptionClick={(option) => setDuplicateTables(option === 'Yes')}
                  searchable={false}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-center mt-5">
            <div>Should the duplicate resource have the same permissions?</div>
            <div className="ml-auto w-[150px]">
              <Select
                options={[
                  { value: 'Yes', label: 'Yes' },
                  { value: 'No', label: 'No' }
                ]}
                optionsSelected={[duplicatePermissions ? 'Yes' : 'No']}
                onOptionClick={(option) => setDuplicatePermissions(option === 'Yes')}
                searchable={false}
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end mt-[30px]">
        <Button internalType="danger" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          className="ml-2.5"
          internalType="accept"
          onClick={() => duplicate()}
          disabled={(targetProjectId === undefined && ['process', 'table'].includes(type)) || saving}
          isLoading={saving}
        >
          Confirm
        </Button>
      </div>
    </Modal>
  )
}

export default Duplicate
